import { Show } from "shared/components";
import { usePropertyFormContext } from "../PropertyFormProvider";
import { PropertyGeneralForm } from "../../PropertyGeneralForm";
import { PropertyGroupInfo } from "./PropertyGroupInfo";
import { ScreeningSettingsForm } from "../../ScreeningSettingsForm";
import { RentalInfoForm } from "../../RentalInfoForm";
import { PlanForm } from "../../PlanForm";
import { AmenitiesForm } from "../../AmenitiesForm";
import { PhotosForm } from "../../PhotosForm";
import { BrandingNote } from "./BrandingNote";
import { PropertyKind } from "../types";
import { UnitForms } from "./UnitForms";
import { instance } from "api/apiBase";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks";
import { useAuthContext } from "providers/Auth";
import { PropertyGroup } from "types/property-group";
import { useMemo } from "react";

export const CreatePropertyForm = () => {
  const { type } = useAuthContext();
  const {
    values,
    handleSubmit,
    handleChange,
    register,
    sessionKey,
    setPhotosUploading
  } = usePropertyFormContext();

  const rawQuery = useMemo(
    () => ({
      owner: values["owner_name"] ?? "",
      city: values["city"],
      state_or_province: values["state_or_province"],
      postal_code: values["postal_code"]
    }),
    [values]
  );

  const [debouncedQuery] = useDebouncedValue(rawQuery, 500);

  const { data: propertyGroup, isLoading: isLoadingPropertyGroup } = useQuery({
    queryKey: ["property-groups-by-address", debouncedQuery],
    queryFn: () =>
      instance
        .get("/portal/v1/property_groups/find", {
          params: debouncedQuery
        })
        .then((res) => {
          handleChange("use_property_group_verification_template")(
            Boolean(res.data)
          );
          return res.data as PropertyGroup;
        }),
    enabled: type.isBoomScreen
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-[822px]">
      <PropertyGeneralForm
        handleChange={handleChange}
        register={register}
        isDefaultProperty={!values.kind}
      />
      <PropertyGroupInfo
        isLoadingPropertyGroup={isLoadingPropertyGroup}
        propertyGroup={propertyGroup}
      />
      <Show if={type.isBoomScreen}>
        <ScreeningSettingsForm
          hasPropertyGroup={Boolean(propertyGroup)}
          selectedTemplateId={values.verification_template_id}
          useGroupTemplate={values.use_property_group_verification_template}
          onTemplateSelect={(id) =>
            handleChange("verification_template_id")(id)
          }
          onUseGroupTemplateChange={(checked) =>
            handleChange("use_property_group_verification_template")(checked)
          }
        />
      </Show>
      <Show if={values.kind === PropertyKind.SingleFamily}>
        <RentalInfoForm register={register} />
        <PlanForm register={register} />
      </Show>

      <Show if={Boolean(values.kind)}>
        <AmenitiesForm
          selectedAmenities={values.amenities}
          register={register}
        />
        <PhotosForm
          onChange={(formattedPhotos) => handleChange("media")(formattedPhotos)}
          sessionKey={sessionKey}
          setPhotosUploading={setPhotosUploading}
        />
        <BrandingNote />
      </Show>

      <Show if={!values.kind}>
        <UnitForms />
      </Show>
    </form>
  );
};
